import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQuery } from '@verantos-dev/core-utils';

import { getAppConfig } from './appConfig';

/**
 * `VITE_USE_PROXY` set to exactly `'true'` routes requests through the
 * relative `/api` prefix instead of the runtime/build-time API URL below.
 * `/api` resolves only under `apps/annotation-tool`'s Vite dev server, which
 * declares a matching `server.proxy['/api']` entry (see its vite.config.ts)
 * that strips the prefix and forwards to the backend so the browser issues a
 * same-origin request and CORS does not apply. The value is read once at
 * module load, so it is fixed for the lifetime of the page.
 *
 * Otherwise, prefers `window.__APP_CONFIG__.baseApiUrl` — written at container
 * start by apps/annotation-tool/infra/40-write-app-config.sh — over the
 * build-time `VITE_BASE_API_URL`. This is what lets one container image serve
 * any PR's preview environment: the API origin differs per PR and is supplied
 * at runtime rather than baked into the bundle. The S3 + CloudFront deployment
 * serves an empty default config (apps/annotation-tool/public/config.js), so
 * `baseApiUrl` is absent there and `VITE_BASE_API_URL` applies as before.
 */
export const getAnnotationToolApiBaseUrl = (): string => {
  if (import.meta.env.VITE_USE_PROXY === 'true') return '/api';
  return getAppConfig()?.baseApiUrl || import.meta.env.VITE_BASE_API_URL;
};

// Example of creating multiple base APIs
export const annotationToolApiV1 = createApi({
  reducerPath: 'annotationToolApiV1',
  baseQuery: createBaseQuery({ version: 'v1', overrideBaseUrl: getAnnotationToolApiBaseUrl() }),
  endpoints: () => ({}), // inject endpoints at the feature level
});

export const annotationToolApi = annotationToolApiV1;
